import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  AlertCircle,
  CheckCircle,
  Store,
  Calendar,
  User,
  Package,
  Home,
  Clock,
} from "lucide-react";
import toast from "react-hot-toast";
import "../styles/ScanPage.css";

// Import Firebase
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  increment,
} from "firebase/firestore";
import { db } from "../lib/firebase";

export default function ScanPage() {
  const { qrId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [qrData, setQrData] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [relayPoints, setRelayPoints] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null);
  const [contactInfo, setContactInfo] = useState({
    name: "",
    phone: "",
    message: "",
  });
  const [selectedRelayPoint, setSelectedRelayPoint] = useState(null);
  const [status, setStatus] = useState(null); // 'unassigned', 'assigned', 'found', 'reported'

  useEffect(() => {
    initializeScan();
  }, [qrId]);

  const initializeScan = async () => {
    setLoading(true);

    try {
      // Récupérer la localisation de l'utilisateur
      const location = await getUserLocation();
      setUserLocation(location);

      // Chercher le QR code dans Firebase
      const qrCodesRef = collection(db, "qrCodes");
      const querySnapshot = await getDocs(qrCodesRef);

      const foundQr = querySnapshot.docs.find(
        (doc) => doc.data().qrId === qrId || doc.id === qrId
      );

      if (!foundQr) {
        setStatus("not_found");
        setLoading(false);
        return;
      }

      // Incrémenter le compteur de scans immédiatement
      const qrRef = doc(db, "qrCodes", foundQr.id);
      await updateDoc(qrRef, {
        lastScan: serverTimestamp(),
        scanCount: increment(1),
        updatedAt: serverTimestamp(),
      });

      // Si c'est le premier scan, enregistrer la date du premier scan
      const data = foundQr.data();
      if (!data.firstScan) {
        await updateDoc(qrRef, {
          firstScan: serverTimestamp(),
        });
      }

      // Récupérer les données mises à jour
      const updatedDoc = await getDoc(qrRef);
      const updatedData = updatedDoc.data();

      const qrInfo = {
        id: foundQr.id,
        ...updatedData,
        ownerPhone: updatedData.ownerPhone || "",
        ownerPhone2: updatedData.ownerPhone2 || "",
        ownerEmail: updatedData.ownerEmail || "",
        createdAt: updatedData.createdAt?.toDate?.() || new Date(),
        lastScan: updatedData.lastScan?.toDate?.() || null,
        firstScan: updatedData.firstScan?.toDate?.() || null,
        assignedAt: updatedData.assignedAt?.toDate?.() || null,
      };

      setQrData(qrInfo);

      // Déterminer le statut
      if (updatedData.status === "unassigned") {
        setStatus("unassigned");
      } else if (updatedData.status === "assigned") {
        setStatus("assigned");
      }

      // Log du scan
      await logScan("page_view", null, location);
    } catch (error) {
      console.error("Scan error:", error);
      toast.error("Erreur lors du scan");
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour logger le scan
  const logScan = async (type, action = null, location = null) => {
    try {
      await addDoc(collection(db, "scans"), {
      
        qrId: qrId,
        type: type,
        action: action,
        timestamp: serverTimestamp(),
        location: location,
        metadata: {
          ip: await getClientIP(),
          userAgent: navigator.userAgent,
          pageUrl: window.location.href,
        },
      });
    } catch (error) {
      console.error("Error logging scan:", error);
    }
  };

  const loadRelayPoints = async (location) => {
    try {
      // Récupérer les points relais depuis Firebase
      const relayPointsRef = collection(db, "relayPoints");
      const querySnapshot = await getDocs(relayPointsRef);

      const points = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Calculer les distances si la localisation est disponible
      if (location?.coordinates) {
        const pointsWithDistance = points.map((point) => {
          if (point.latitude && point.longitude) {
            const distance = calculateDistance(
              location.coordinates.latitude,
              location.coordinates.longitude,
              point.latitude,
              point.longitude
            );
            return { ...point, distance: distance.toFixed(1) };
          }
          return point;
        });

        // Trier par distance
        pointsWithDistance.sort(
          (a, b) => (a.distance || 999) - (b.distance || 999)
        );
        setRelayPoints(pointsWithDistance.slice(0, 5)); // Limiter à 5 points
      } else {
        setRelayPoints(points.slice(0, 5));
      }
    } catch (error) {
      console.error("Error loading relay points:", error);
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            coordinates: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
            timestamp: new Date().toISOString(),
          });
        },
        (error) => {
          console.warn("Geolocation error:", error);
          resolve(null);
        },
        { timeout: 5000 }
      );
    });
  };

  const handleReportFound = async () => {
    if (!selectedAction) {
      toast.error("Veuillez choisir une action");
      return;
    }

    if (selectedAction === "contact_owner" && !contactInfo.phone) {
      toast.error("Veuillez fournir un numéro de téléphone");
      return;
    }

    if (selectedAction === "deposit_relay" && !selectedRelayPoint) {
      toast.error("Veuillez sélectionner un point relais");
      return;
    }

    try {
      const qrRef = doc(db, "qrCodes", qrData.id);

      // Mettre à jour le QR code
      await updateDoc(qrRef, {
        lastScan: serverTimestamp(),
        scanCount: increment(1),
        isFound: true,
        foundAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "scans"), {
        qrCodeId: qrData.id,
        qrId: qrData.qrId,
        type: "found",
        action: selectedAction,
        timestamp: serverTimestamp(),
        finderInfo: contactInfo,
        location: userLocation,
        relayPoint: selectedRelayPoint,
        objectName: qrData.objectName,
        objectType: qrData.objectType,
        // Ajouter tous les contacts
        ownerEmail: qrData.ownerEmail,
        ownerPhone: qrData.ownerPhone,
        ownerPhone2: qrData.ownerPhone2, // Ajouter le second numéro
        metadata: {
          ip: await getClientIP(),
          userAgent: navigator.userAgent,
        },
      });

      // Envoyer une notification au propriétaire
      await sendOwnerNotification(
        selectedAction,
        contactInfo,
        selectedRelayPoint
      );

      toast.success("Propriétaire alerté avec succès !");
      setStatus("reported");
    } catch (error) {
      console.error("Report error:", error);
      toast.error("Erreur lors du signalement");
    }
  };


  const handleReportContacted = async () => {
   
    
    try {
      const qrRef = doc(db, 'qrCodes', qrData.id)
      
      // Mettre à jour le statut du QR code
      await updateDoc(qrRef, {
        updatedAt: serverTimestamp(),
        isFound: true,
      })
      
      // Enregistrer l'action de contact
      await addDoc(collection(db, 'scans'), {
        qrCodeId: qrData.id,
        qrId: qrData.qrId,
        type: 'owner_contacted',
        action: 'contact_owner',
        
        timestamp: serverTimestamp(),
        finderInfo: {
          contactTime: new Date().toISOString()
        },
        location: userLocation,
        objectName: qrData.objectName,
        objectType: qrData.objectType,
        ownerEmail: qrData.ownerEmail,
        ownerPhone: qrData.ownerPhone,
        ownerPhone2: qrData.ownerPhone2,
        metadata: {
          ip: await getClientIP(),
          userAgent: navigator.userAgent
        }
      })
      
      toast.success('Merci pour votre aide ! Le propriétaire a été notifié.')
      setStatus('contacted')
      
    } catch (error) {
      console.error('Report error:', error)
      toast.error('Erreur lors de l\'enregistrement')
    }
  }
  const getClientIP = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return "unknown";
    }
  };

  const sendOwnerNotification = async (action, finderInfo, relayPoint) => {
    console.log("Notification envoyée au propriétaire:", {
      ownerEmail: qrData.ownerEmail,
      ownerPhone: qrData.ownerPhone,
      ownerPhone2: qrData.ownerPhone2, // Ajout du second numéro
      action,
      finderInfo,
      relayPoint,
      objectName: qrData.objectName,
      scanTime: new Date().toISOString(),
    });

    // Simulation d'envoi d'email
    if (qrData.ownerEmail) {
      console.log(`Email envoyé à ${qrData.ownerEmail}`);
    }

    // Simulation d'envoi de SMS au premier numéro
    if (qrData.ownerPhone) {
      console.log(`SMS envoyé au premier numéro: ${qrData.ownerPhone}`);
    }

    // Simulation d'envoi de SMS au second numéro
    if (qrData.ownerPhone2) {
      console.log(`SMS envoyé au second numéro: ${qrData.ownerPhone2}`);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="scan-loading">
          <div className="spinner-large"></div>
          <p className="loading-text">Analyse du QR code...</p>
        </div>
      );
    }

    switch (status) {
      case "unassigned":
        return (
          <div className="scan-result unassigned">
            <CheckCircle size={64} className="result-icon success" />
            <h2 className="result-title">QR Code disponible</h2>
            <p className="result-description">
              Ce QR code n'est pas encore assigné. Si vous êtes le propriétaire
              de l'objet, vous pouvez l'assigner pour le protéger.
            </p>
            <div className="result-actions">
              <button
                onClick={() => navigate(`/assign/${qrId}`)}
                className="btn btn-primary2 btn-lg"
              >
                Assigner cet objet
              </button>
              <button onClick={() => navigate("/")} className="btn btn-outline">
                Retour à l'accueil
              </button>
            </div>
          </div>
        );

      case "assigned":
        return (
          <div className="scan-result assigned">
    <div className="result-header">
      <AlertCircle size={64} className="result-icon warning" />
      <h2 className="result-title">Objet perdu !</h2>
      <p className="result-description">
        Cet objet a été signalé comme perdu. Vous pouvez aider à le rendre à son propriétaire.
      </p>
    </div>
    
    <div className="object-info-card">
      <h3 className="card-title">
        <Package size={20} />
        Informations de l'objet
      </h3>
      <div className="object-details">
        <div className="detail-item">
          <strong>Nom :</strong>
          <span>{qrData.objectName}</span>
        </div>
        {qrData.description && (
          <div className="detail-item">
            <strong>Description :</strong>
            <span>{qrData.description}</span>
          </div>
        )}
        {qrData.objectType && (
          <div className="detail-item">
            <strong>Type :</strong>
            <span className="object-type">{qrData.objectType}</span>
          </div>
        )}
        {/* {qrData.assignedAt && (
          <div className="detail-item">
            <Calendar size={16} />
            <span>Signalé perdu le {qrData.assignedAt.toLocaleDateString('fr-FR')}</span>
          </div>
        )} */}
      </div>
    </div>
    
    <div className="action-selection">
      <h3 className="section-title">Comment souhaitez-vous aider ?</h3>
      <div className="action-grid">
        <button
          className={`action-card ${selectedAction === 'contact_owner' ? 'selected' : ''}`}
          onClick={() => setSelectedAction('contact_owner')}
        >
          <div className="action-icon">
            <Phone size={24} />
          </div>
          <h4 className="action-title">Contacter le propriétaire</h4>
          <p className="action-description">
            Obtenez les coordonnées du propriétaire pour organiser la restitution
          </p>
        </button>
        
        <button
          className="action-card disabled"
          disabled
          title="Bientôt disponible"
        >
          <div className="action-icon">
            <Store size={24} />
          </div>
          <h4 className="action-title">Déposer en point relais</h4>
          <p className="action-description">
            Déposez l'objet dans un point relais sécurisé
          </p>
          <div className="coming-soon-badge">
            <span>Bientôt</span>
          </div>
        </button>
      </div>
    </div>
    
    {/* Formulaire de contact - affiché uniquement quand l'option est sélectionnée */}
    {selectedAction === 'contact_owner' && (
      <>
        <div className="owner-contact-reveal">
          <div className="reveal-header">
            <CheckCircle size={20} />
            <h3>Coordonnées du propriétaire</h3>
          </div>
          <p className="reveal-note">
            Voici les coordonnées du propriétaire. Vous pouvez le contacter directement.
          </p>
          
          <div className="owner-contact-list">
            {/* Premier numéro */}
            {qrData.ownerPhone && (
              <div className="contact-item">
                <div className="contact-icon">
                  <Phone size={18} />
                </div>
                <div className="contact-info">
                  <span className="contact-label">Téléphone principal</span>
                  <div className="contact-value-wrapper">
                    <span className="contact-value">{qrData.ownerPhone}</span>
                    <button 
                      className="copy-btn2"
                      onClick={() => {
                        navigator.clipboard.writeText(qrData.ownerPhone)
                        toast.success('Numéro copié')
                      }}
                    >
                      Copier
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Second numéro */}
            {qrData.ownerPhone2 && (
              <div className="contact-item">
                <div className="contact-icon">
                  <Phone size={18} />
                </div>
                <div className="contact-info">
                  <span className="contact-label">Téléphone secondaire</span>
                  <div className="contact-value-wrapper">
                    <span className="contact-value">{qrData.ownerPhone2}</span>
                    <button 
                      className="copy-btn2"
                      onClick={() => {
                        navigator.clipboard.writeText(qrData.ownerPhone2)
                        toast.success('Numéro copié')
                      }}
                    >
                      Copier
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Email */}
            {qrData.ownerEmail && (
              <div className="contact-item">
                <div className="contact-icon">
                  <Mail size={18} />
                </div>
                <div className="contact-info">
                  <span className="contact-label">Email</span>
                  <div className="contact-value-wrapper">
                    <span className="contact-value">{qrData.ownerEmail}</span>
                    <button 
                      className="copy-btn2"
                      onClick={() => {
                        navigator.clipboard.writeText(qrData.ownerEmail)
                        toast.success('Email copié')
                      }}
                    >
                      Copier
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="contact-guidelines">
            <h4>Recommandations :</h4>
            <ul>
              <li>Présentez-vous poliment comme ayant trouvé l'objet</li>
              <li>Proposez un lieu public et sûr pour la remise</li>
              <li>Gardez l'objet en sécurité en attendant</li>
              <li>Ne divulguez pas d'informations personnelles sensibles</li>
            </ul>
          </div>
        </div>
        
        {/* Optionnel : Formulaire pour indiquer que vous avez contacté */}
        <div className="contact-form-card">
          <h3 className="card-title">
            <CheckCircle size={20} />
            J'ai contacté le propriétaire
          </h3>
          <p className="form-description">
            Une fois que vous avez contacté le propriétaire, vous pouvez nous le signaler pour la confirmation.
          </p>
          
          <div className="form-actions">
            <button
              onClick={() => handleReportContacted()}
              className="submit-btn secondary"
            >
              J'ai contacté le propriétaire
            </button>
          </div>
        </div>
      </>
    )}
    
    {/* Message si aucune action sélectionnée */}
    {!selectedAction && (
      <div className="action-instruction">
        <p>Veuillez sélectionner une option ci-dessus pour continuer.</p>
      </div>
    )}
  </div>
        );

 
        // Modifiez le switch/case dans renderContent
case 'contacted':
  return (
    <div className="scan-result contacted">
      <CheckCircle size={64} className="result-icon success" />
      <h2 className="result-title">Merci pour votre aide !</h2>
      <p className="result-description">
        Votre signalement a été enregistré. Le propriétaire a été notifié que vous l'avez contacté.
      </p>
      
      <div className="next-steps-card">
        <h3 className="card-title">Prochaines étapes</h3>
        <ul className="steps-list">
          <li className="step-item">
            <CheckCircle size={16} />
            <div className="step-content">
              <strong>Organisez la remise</strong>
              <p>Mettez-vous d'accord sur un lieu public et sûr pour rendre l'objet</p>
            </div>
          </li>
          <li className="step-item">
            <Package size={16} />
            <div className="step-content">
              <strong>Conservez l'objet</strong>
              <p>Gardez l'objet en sécurité jusqu'à la remise</p>
            </div>
          </li>
          <li className="step-item">
            <AlertCircle size={16} />
            <div className="step-content">
              <strong>En cas de problème</strong>
              <p>Si le propriétaire ne répond pas, vous pouvez contacter le support</p>
            </div>
          </li>
        </ul>
      </div>
      
      <div className="result-actions">
        <button 
          onClick={() => navigate('/')}
          className="btn btn-primary2"
        >
          <Home size={20} />
          Retour à l'accueil
        </button>
      </div>
    </div>
  )

      case "not_found":
        return (
          <div className="scan-result not-found">
            <AlertCircle size={64} className="result-icon error" />
            <h2 className="result-title">QR Code non reconnu</h2>
            <p className="result-description">
              Ce QR code ne correspond à aucun objet enregistré dans notre
              système.
            </p>
            <div className="result-actions">
              <button onClick={() => navigate("/")} className="btn btn-primary2">
                Retour à l'accueil
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="scan-result error">
            <AlertCircle size={64} className="result-icon error" />
            <h2 className="result-title">Erreur</h2>
            <p className="result-description">
              Une erreur est survenue lors du scan du QR code.
            </p>
            <div className="result-actions">
              <button
                onClick={() => window.location.reload()}
                className="btn btn-primary"
              >
                Réessayer
              </button>
              <button onClick={() => navigate("/")} className="btn btn-outline2">
                Retour à l'accueil
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="scan-page">
      <header className="scan-header">
        <div className="container">
          <div className="header-content">
            <div className="brand">
              <h1>UltimaTag</h1>
              {/* <p className="brand-subtitle">Scanner un QR code</p> */}
            </div>
            {/* <div className="qr-info">
              <span className="qr-label">Code:</span>
              <code className="qr-id">{qrId}</code>
            </div> */}
          </div>
        </div>
      </header>

      <main className="scan-main">
        <div className="container">
          <div className="scan-container">{renderContent()}</div>
        </div>
      </main>

      <footer className="scan-footer">
        <div className="container">
          <p>
            © 2024 UltimaTag. Scanner un QR code pour aider à retrouver un objet
            perdu.
          </p>
        </div>
      </footer>
    </div>
  );
}
